import { Metric } from '@fullstory/proto/fs/api/metrics';
import { TimeInterval } from '@fullstory/proto/fs/api/segment';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import * as searchSettings from '@fullstory/proto/fs/services/search-settings';
import {
  Color,
  DimensionalitySettings,
  SingleNumberSettings,
  TrendFrequency,
  TrendSettings,
} from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { getSegmentArrayFromVizSettings } from '@fullstory/segments-model';
import { isEqual } from 'lodash';
import { DEFAULT_DISPLAY, DEFAULT_MEASURE } from '../constants';
import { getDisplayForMetric, nullDimensionSettings } from '../utils';
import { MetricFormType } from './metricForm';
import { DEFAULT_ROLLING_WINDOW, getRollingWindow } from './rollingWindow';
import { UnnamedMetricFormValues } from './unnamedMetric';

// Translations from stored TrendFrequency (cards) to Metric API's TimeInterval
export const FREQUENCY_TO_INTERVAL = {
  [TrendFrequency.TREND_FREQUENCY_MINUTELY]: TimeInterval.TIME_INTERVAL_MINUTE,
  [TrendFrequency.TREND_FREQUENCY_HOURLY]: TimeInterval.TIME_INTERVAL_HOUR,
  [TrendFrequency.TREND_FREQUENCY_DAILY]: TimeInterval.TIME_INTERVAL_DAY,
  [TrendFrequency.TREND_FREQUENCY_WEEKLY]: TimeInterval.TIME_INTERVAL_WEEK,
  [TrendFrequency.TREND_FREQUENCY_MONTHLY]: TimeInterval.TIME_INTERVAL_MONTH,
  [TrendFrequency.TREND_FREQUENCY_INVALID]: TimeInterval.TIME_INTERVAL_INVALID,
};

export const INTERVAL_TO_FREQUENCY = {
  [TimeInterval.TIME_INTERVAL_MINUTE]: TrendFrequency.TREND_FREQUENCY_MINUTELY,
  [TimeInterval.TIME_INTERVAL_HOUR]: TrendFrequency.TREND_FREQUENCY_HOURLY,
  [TimeInterval.TIME_INTERVAL_DAY]: TrendFrequency.TREND_FREQUENCY_DAILY,
  [TimeInterval.TIME_INTERVAL_WEEK]: TrendFrequency.TREND_FREQUENCY_WEEKLY,
  [TimeInterval.TIME_INTERVAL_MONTH]: TrendFrequency.TREND_FREQUENCY_MONTHLY,
  [TimeInterval.TIME_INTERVAL_ENTIRE_RANGE]: TrendFrequency.TREND_FREQUENCY_INVALID,
  [TimeInterval.TIME_INTERVAL_INVALID]: TrendFrequency.TREND_FREQUENCY_INVALID,
};

export const getVisualizationSettings = (
  metric: searchSettings.Metric | null | undefined,
): TrendSettings | SingleNumberSettings | DimensionalitySettings | null => {
  if (!metric) return null;
  if (metric.trend) {
    return metric.trend;
  }
  if (metric.singleNumber) {
    return metric.singleNumber;
  }
  if (metric.dimensionality) {
    return metric.dimensionality;
  }
  return null;
};

export const getSavedMetricVisualizationSettings = (
  metricRecord: searchSettings.MetricRecord | null | undefined,
): TrendSettings | SingleNumberSettings | DimensionalitySettings | null => {
  if (!metricRecord) return null;
  return getVisualizationSettings(metricRecord.metric);
};

export const buildUnnamedMetric = (
  metricRecord: searchSettings.MetricRecord | null | undefined,
  metricId: string | undefined,
): UnnamedMetricFormValues | undefined => {
  const innerMetric = metricRecord?.metric?.metric;
  if (!innerMetric) {
    return;
  }
  const vizSettings = getSavedMetricVisualizationSettings(metricRecord);
  const segments = getSegmentArrayFromVizSettings(vizSettings?.segment);
  const timeRange = vizSettings?.timeRange ?? null;
  const interval = metricRecord?.metric?.trend?.frequency
    ? FREQUENCY_TO_INTERVAL[metricRecord?.metric.trend.frequency]
    : TimeInterval.TIME_INTERVAL_ENTIRE_RANGE;
  const dimension =
    metricRecord?.metric?.trend?.dimensionSettings ??
    metricRecord?.metric?.dimensionality?.dimensionSettings ??
    nullDimensionSettings;
  const singleNumberColor = metricRecord?.metric?.singleNumber?.color;
  const rollingWindow = metricRecord?.metric?.metric
    ? getRollingWindow(metricRecord.metric.metric)
    : DEFAULT_ROLLING_WINDOW;

  const metric: MetricFormType = {
    ...metricRecord,
    id: metricId ?? '',
    name: metricRecord?.metric?.name ?? '',
    description: metricRecord?.metric?.description ?? '',
    metric: innerMetric,
    display:
      vizSettings?.displayAs ??
      getDisplayForMetric(DEFAULT_DISPLAY, metricRecord?.metric!.metric!, true),
    archived: false,
    segment: segments,
    timeRange,
    timeRangeComparison: vizSettings?.timeRangeComparison ?? null,
    interval,
    dimension,
    singleNumberColor: singleNumberColor ?? Color.COLOR_INVALID,
    rollingWindow,
    visibility: Visibility.UNSET,
  };

  const unnamedMetricFormValues = {
    metric,
    segments,
    timeRange,
    timeInterval: interval,
    dimensionSettings: dimension,
    metricId: metricId ?? '',
    timeRangeComparison: vizSettings?.timeRangeComparison ?? null,
    unnamedMetric: null,
    singleNumberColor: singleNumberColor ?? Color.COLOR_INVALID,
    rollingWindow,
  };
  return unnamedMetricFormValues;
};

export const DEFAULT_METRIC: Metric = {
  single: DEFAULT_MEASURE,
};

export const isDefaultMetric = (metric: Metric | null | undefined): boolean =>
  isEqual(metric, DEFAULT_METRIC);
